import React, { useMemo, useState, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { styled, Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import { ExpressionPath, FilterPath, ExpSide, isExpressionPath, ExpressionFunctionSpec, FuncArgPath, PathLocation, PathType, isLiteral, typesMap } from '../types';
import { getReturnType } from '../utils';
import { FilterContext } from './FilterContainer';
import { GridScope, GridContext } from '../../components/DataGrid';
import { AttrPath } from '../../components/AttrSelectorProvider';
import FunctionSelectorProvider from './FunctionSelectorProvider';
import ExpressionFuncArgs from './ExpressionFuncArgs';
import { normalProps, selectedProps } from './OpSelector';
import * as actions from '../actions';
import PathOrLiteralSelector from './PathOrLiteralSelector'


export const Item = styled(Box)<{ ownerState: { disabled: boolean } }>(({ theme, ownerState }) => ({
    backgroundColor: !ownerState?.disabled ? (theme.palette.mode === 'dark' ? '#1A2027' : '#fff') : theme.palette.grey[200],
    ...theme.typography.body2,
    padding: theme.spacing(1),
    margin: theme.spacing(1),
    textAlign: 'center',
    color: !ownerState?.disabled ? theme.palette.text.secondary : theme.palette.grey[600],
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    boxShadow: theme.shadows[3],
    borderRadius: theme.spacing(1),
    cursor: 'default',
    position: 'relative',
    userSelect: 'none',
}));

const ControlElBase = styled(Item)(({ theme }) => ({
    padding: theme.spacing(1),
    display: 'block',
    position: 'absolute',
    width: theme.spacing(1),
    height: theme.spacing(1),
}));

const ControlElRight = styled(ControlElBase)(({ theme }) => ({
    top: '50%',
    right: '-' + theme.spacing(2),
    transform: 'translateY(-100%)'
}));

const ControlElTopCenter = styled(ControlElBase)(({ theme }) => ({
    top: '-' + theme.spacing(2),
    left: '50%',
    transform: 'translateX(-100%)'
}));

const ExpressionPathBuilder: React.FC<{
    expressionPath: ExpressionPath,
    filterPath: FilterPath,
    side: ExpSide,
    rhsIndex?: number,
    depth?: number,
    argPath?: FuncArgPath,
    hidden: boolean
}> = ({
    expressionPath,
    filterPath,
    side,
    rhsIndex,
    depth = 0,
    argPath = [],
    hidden
}) => {
        const dispatch = useDispatch();
        const gridScope: GridScope = useContext(GridContext)!;
        const { disabled } = useContext(FilterContext)!;
        const [anchorAttrEl, setAnchorAttrEl] = useState<HTMLDivElement | null>(null);
        const [anchorFuncEl, setAnchorFuncEl] = useState<HTMLDivElement | null>(null);

        const pathLocation: PathLocation = useMemo(
            () => ({ filterPath, side, argPath, depth, rhsIndex }),
            [filterPath, side, argPath, depth, rhsIndex]
        );

        const type: PathType | null = useMemo(
            () => {
                return getReturnType(expressionPath);
            },
            [expressionPath]
        );

        const handleSelectPath = (event: React.MouseEvent<HTMLDivElement>) => {
            if (!disabled) {
                setAnchorAttrEl(event.currentTarget);
            }
            event.stopPropagation();
        };

        const handlePathSelected = (attr: AttrPath) => {
            if (expressionPath.path != attr) {
                dispatch(actions.setPath(pathLocation, attr.path, attr.field));
            }
        };

        const handleLiteralSet = (value: string) => {
            dispatch(actions.setLiteral(pathLocation, value));
        };

        const handleSelectFunc = (event: React.MouseEvent<HTMLDivElement>) => {
            if (!disabled) {
                setAnchorFuncEl(event.currentTarget);
            }
            event.stopPropagation();
        };

        const handleFuncSelected = (spec: ExpressionFunctionSpec) => {
            if (expressionPath.func == null || expressionPath.func.spec.name != spec.name) {
                dispatch(actions.setFunc(pathLocation, spec));
            }
        };

        const handleRemoveFunc = (event: React.MouseEvent<SVGSVGElement>) => {
            dispatch(actions.removeFunc(pathLocation));
            event.stopPropagation();
        }

        const sxProps: SxProps<Theme> = {
            fontSize: '12px',
            color: !disabled ? 'black' : 'grey',
            position: 'absolute',
            top: 2,
            left: 2
        };

        const render = () => <>
            {
                isExpressionPath(expressionPath.path) ?
                    <ExpressionPathBuilder
                        expressionPath={expressionPath.path}
                        filterPath={filterPath}
                        side={side}
                        depth={depth + 1}
                        argPath={argPath}
                        hidden={hidden}
                        rhsIndex={rhsIndex}
                    />
                    :
                    isLiteral(expressionPath.path) ?
                        <Box onClick={handleSelectPath}>{`"${expressionPath.path.value}"`}</Box>
                        :
                        <Box onClick={handleSelectPath}>{expressionPath.path}</Box>
            }

            {
                expressionPath.func && <>
                    <Box onClick={handleSelectFunc} sx={{ position: 'relative' }}>
                        {`.${expressionPath.func.spec.name}(`}
                        {
                            <ControlElTopCenter ownerState={{ disabled }}>
                                <CloseIcon fontSize='small' sx={sxProps} onClick={handleRemoveFunc} />
                            </ControlElTopCenter>
                        }
                    </Box>
                    <ExpressionFuncArgs
                        expressionPath={expressionPath}
                        filterPath={filterPath}
                        side={side}
                        depth={depth}
                        argPath={argPath}
                        hidden={hidden}
                        rhsIndex={rhsIndex}
                    />
                    {`)`}
                </>
            }

            {
                expressionPath.path && !expressionPath.func && !isLiteral(expressionPath.path) && <ControlElRight ownerState={{ disabled }} onClick={handleSelectFunc}>
                    <Typography sx={{
                        position: 'absolute',
                        top: -3,
                        left: 5,
                        fontWeight: 'bold'
                    }} >{'f'}</Typography>
                </ControlElRight>
            }
        </>

        return <>
            {
                depth == 0 ? <Item ownerState={{ disabled }} onClick={expressionPath.path ? undefined : handleSelectPath}>
                    {render()}
                </Item> : render()
            }

            <PathOrLiteralSelector
                anchorEl={anchorAttrEl}
                listConf={gridScope.conf}
                onClose={() => {
                    setAnchorAttrEl(null);
                }}
                pathValue={expressionPath.path}
                handlePathSelected={handlePathSelected}
                handleLiteralSet={handleLiteralSet}
                defaultLiteral={side === ExpSide.right}
                hidden={hidden}
            />

            {
                type != null && <FunctionSelectorProvider
                    anchorEl={anchorFuncEl}
                    type={type}
                    onClose={() => {
                        setAnchorFuncEl(null);
                    }}
                >
                    {({
                        spec,
                        id,
                        onClose,
                    }) => {
                        return <ListItem
                            disablePadding
                            key={spec.name + spec.args.length}
                            sx={expressionPath.func?.spec.name === spec.name ? selectedProps : normalProps}
                            onClick={(event: React.MouseEvent<HTMLLIElement>) => {
                                handleFuncSelected(spec);
                                event.stopPropagation();
                                onClose();
                            }}
                        >
                            <ListItemButton>
                                <ListItemText id={id} primary={`${spec.name}`} />
                            </ListItemButton>
                        </ListItem>
                    }}
                </FunctionSelectorProvider>
            }
        </>
    }

export default ExpressionPathBuilder;
